import { useState, useEffect } from 'react';
import { X, Gift } from 'lucide-react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });

const FreeCasePromo = ({ promo, onDismiss }) => (
  <div className="fixed top-20 right-4 z-50 max-w-sm w-full animate-fade-in-up" data-testid="free-case-promo">
    <div className="bg-[#0A0A0A] border-2 border-[#C41E3A] rounded-lg shadow-2xl overflow-hidden" style={{ boxShadow: '0 0 30px rgba(196,30,58,0.45)' }}>
      <div className="bg-[#C41E3A] px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gift size={15} className="text-white" />
          <span className="text-white text-xs font-bold uppercase tracking-wider">
            Limited Time • {fmtDate(promo.start_date)} – {fmtDate(promo.end_date)}
          </span>
        </div>
        <button onClick={onDismiss} className="text-white/70 hover:text-white transition-colors" aria-label="Close" data-testid="free-case-promo-close">
          <X size={16} />
        </button>
      </div>
      <div className="p-4">
        <div className="flex gap-4 items-center">
          <div className="w-24 h-24 flex-shrink-0 bg-white rounded-md overflow-hidden border border-[#D4AF37]/40">
            <img src={promo.free_item.image} alt={promo.free_item.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <h3 className="font-['Oswald'] text-xl uppercase tracking-wide text-white leading-tight">
              Free <span className="text-[#C41E3A]">{promo.free_item.name}</span>
            </h3>
            <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider mt-1">
              Spend ${Math.round(promo.min_spend)}+ on peptides
            </p>
          </div>
        </div>
        <p className="text-gray-300 text-xs leading-relaxed mt-3">
          Get our {promo.free_item.name.toLowerCase()} ({promo.free_item.dosage.toLowerCase()}) <span className="text-green-400 font-semibold">FREE</span> when your peptide total hits <span className="text-[#D4AF37] font-semibold">${Math.round(promo.min_spend)} or more</span> (before shipping — accessories &amp; skincare not included).
        </p>
        <div className="bg-[#121212] border border-dashed border-[#D4AF37] rounded-sm px-3 py-2 mt-3 text-center">
          <p className="text-gray-400 text-[10px] uppercase tracking-widest">Use code at checkout</p>
          <p className="text-[#D4AF37] text-lg font-bold tracking-[0.3em]" data-testid="free-case-promo-code">{promo.code}</p>
        </div>
        <Link
          to="/#products"
          onClick={onDismiss}
          className="block w-full text-center bg-[#C41E3A] hover:bg-[#A01830] text-white text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm mt-3 transition-all duration-300"
          data-testid="free-case-promo-shop-btn"
        >
          Shop Peptides
        </Link>
      </div>
    </div>
  </div>
);

export const ComingSoonBanner = () => {
  const [visible, setVisible] = useState(false);
  const [promo, setPromo] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();
  const hiddenRoutes = ['/admin', '/cart'];

  useEffect(() => {
    axios.get(`${API}/promo-config`)
      .then(res => setPromo(res.data))
      .catch(() => setPromo(null))
      .finally(() => setLoaded(true));
  }, []);

  const promoActive = !!(promo?.active && promo?.free_item);
  const storageKey = 'free-case-promo-dismissed';

  useEffect(() => {
    if (!loaded || !promoActive) return;
    const wasDismissed = sessionStorage.getItem(storageKey);
    if (wasDismissed) return;
    const showTimer = setTimeout(() => setVisible(true), 1500);
    return () => { clearTimeout(showTimer); };
  }, [loaded, promoActive]);

  const handleDismiss = () => {
    setVisible(false);
    sessionStorage.setItem(storageKey, 'true');
  };

  if (!promoActive || !visible || hiddenRoutes.includes(location.pathname)) return null;

  return <FreeCasePromo promo={promo} onDismiss={handleDismiss} />;
};

export default ComingSoonBanner;
